import { useEffect, useState } from "react";
import { Crown, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LINKS = [
  { href: "#about", label: "About", testid: "nav-link-about" },
  { href: "#pageants", label: "Pageants", testid: "nav-link-pageants" },
  { href: "#gallery", label: "Gallery", testid: "nav-link-gallery" },
  { href: "#stories", label: "Stories", testid: "nav-link-stories" },
  { href: "#news", label: "News", testid: "nav-link-news" },
  { href: "#contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-black/60 border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-10">
          <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
            <Crown className="h-6 w-6 text-[#D4AF37]" strokeWidth={1} />
            <span className="font-script text-3xl leading-none text-[#D4AF37]">D'La Valentina</span>
          </a>
          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={l.testid}
                className="text-xs uppercase tracking-[0.25em] text-neutral-300 transition-colors duration-300 hover:text-[#D4AF37]"
              >
                {l.label}
              </a>
            ))}
            <a
              href="/register"
              data-testid="nav-cta-button"
              className="border border-[#D4AF37]/60 px-6 py-2.5 text-xs uppercase tracking-[0.25em] text-[#D4AF37] transition-all duration-300 hover:bg-[#D4AF37] hover:text-black"
            >
              Register
            </a>
          </nav>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(true)}
            className="text-[#D4AF37] lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-7 w-7" strokeWidth={1} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/95 backdrop-blur-xl"
          >
            <button
              data-testid="nav-mobile-close-button"
              onClick={() => setOpen(false)}
              className="absolute right-6 top-6 text-[#D4AF37]"
              aria-label="Close menu"
            >
              <X className="h-8 w-8" strokeWidth={1} />
            </button>
            {LINKS.map((l, i) => (
              <motion.a
                key={l.href}
                href={l.href}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.6 }}
                className="font-display py-3 text-4xl text-neutral-200 transition-colors hover:text-[#D4AF37]"
              >
                {l.label}
              </motion.a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
